import { Box, Flex, Grid, Heading } from '@chakra-ui/react';
import { VisuallyHidden } from '@chakra-ui/visually-hidden';
import { SITE_TITLE } from 'constants/seo';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid3x3, Music, GraduationCap, Trophy, Globe } from 'lucide-react';
import { CategoryCard } from './CategoryCard/CategoryCard';
import { EventList } from './EventList/EventList';
import { FeaturedEventsCarousel } from './FeaturedEventsCarousel/FeaturedEventsCarousel';
import { InternationalEventsSection } from './InternationalEventsSection/InternationalEventsSection';
import { InternationalFilteredEvents } from './InternationalFilteredEvents/InternationalFilteredEvents';
import { EventCategory, useAllEventsQuery } from './hooks/useAllEvents';
import { MatchState } from 'api/responseTypes';
import { useEventTracking } from 'hooks/useEventTracking';
import { useInternationalEventsQuery } from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { resolveInternationalEventsEnabled } from 'utils/internationalEvents';

export const CategoriesHomePage = () => {
  const [selectedCategory, setSelectedCategory] = useState<EventCategory>('all');
  const { isLoading, data } = useAllEventsQuery();
  const { eventTracking } = useEventTracking();

  const {
    items: internationalItems,
    mode: internationalMode,
    isCompactList: isInternationalCompactList,
    isLoading: isInternationalLoading,
  } = useInternationalEventsQuery();
  const isInternationalEnabled = resolveInternationalEventsEnabled(internationalMode);

  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'home_page',
      url: window.location.href,
    });
  }, [eventTracking]);

  const handleCategorySelect = (category: EventCategory) => {
    eventTracking.eventCategory.clicked({
      categoryId: category,
      categoryName: category,
    });

    setSelectedCategory(category);
  };

  // Filter preview events
  const previewConcerts = data?.concerts.filter((c) => c.state === MatchState.PREVIEW) || [];
  const previewWorkshops = data?.workshops.filter((w) => w.state === MatchState.PREVIEW) || [];
  const previewMatches = data?.matches.filter((m) => m.state === MatchState.PREVIEW) || [];

  // Filter out preview events from main events
  const nonPreviewConcerts = data?.concerts.filter((c) => c.state !== MatchState.PREVIEW) || [];
  const nonPreviewWorkshops = data?.workshops.filter((w) => w.state !== MatchState.PREVIEW) || [];
  const nonPreviewMatches = data?.matches.filter((m) => m.state !== MatchState.PREVIEW) || [];

  // In card (non-compact) mode international events are mixed into "all"; in compact
  // mode they live only in their own tab, so they don't add to the "all" count.
  const mixedInternationalCount =
    isInternationalEnabled && !isInternationalCompactList ? internationalItems.length : 0;

  const categories = [
    {
      id: 'all' as EventCategory,
      title: <FormattedMessage defaultMessage='جميع الفعاليات' id='homepage.categories.all' />,
      count:
        nonPreviewConcerts.length +
        nonPreviewWorkshops.length +
        nonPreviewMatches.length +
        mixedInternationalCount,
      icon: Grid3x3,
    },
    {
      id: 'concerts' as EventCategory,
      title: <FormattedMessage defaultMessage='أحداث' id='homepage.categories.events' />,
      count: nonPreviewConcerts.length,
      icon: Music,
    },
    {
      id: 'workshops' as EventCategory,
      title: <FormattedMessage defaultMessage='التعليم' id='homepage.categories.workshops' />,
      count: nonPreviewWorkshops.length,
      icon: GraduationCap,
    },
    {
      id: 'matches' as EventCategory,
      title: <FormattedMessage defaultMessage='المباريات' id='homepage.categories.matches' />,
      count: nonPreviewMatches.length,
      icon: Trophy,
    },
    ...(isInternationalEnabled
      ? [
          {
            id: 'international' as EventCategory,
            title: (
              <FormattedMessage
                defaultMessage='فعاليات دولية'
                id='homepage.categories.international'
              />
            ),
            count: internationalItems.length,
            icon: Globe,
          },
        ]
      : []),
  ];

  const hasPreviewEvents =
    previewConcerts.length > 0 || previewWorkshops.length > 0 || previewMatches.length > 0;

  return (
    <Box bg='BG100' minH='100vh' width='100vw'>
      <VisuallyHidden as='h1' id='headingHomeMain'>
        {SITE_TITLE}
      </VisuallyHidden>

      <Box mb={{ base: 6, md: 10 }}>
        <Box as='section' aria-labelledby='headingFeatured'>
          <VisuallyHidden as='h2' id='headingFeatured'>
            <FormattedMessage
              id='homepage.featured.sectionHeading'
              defaultMessage='الفعاليات المميزة'
            />
          </VisuallyHidden>
          <FeaturedEventsCarousel />
        </Box>
      </Box>

      {/* Preview Events Section */}
      {hasPreviewEvents && (
        <Box
          as='section'
          aria-labelledby='headingPreviewEvents'
          width='100vw'
          px={{ base: 4, md: 6 }}
          py={{ base: 6, md: 8 }}
        >
          <Heading
            as='h2'
            id='headingPreviewEvents'
            variant={{ base: 'headingMedium', md: 'headingLarge' }}
            textAlign='center'
            mb={{ base: 6, md: 8 }}
          >
            <FormattedMessage
              defaultMessage='ترقب الاحداث القادمة خليك ويانا'
              id='homepage.preview.heading'
            />
          </Heading>

          <EventList
            concerts={previewConcerts}
            workshops={previewWorkshops}
            matches={previewMatches}
            selectedCategory='all'
            isLoading={isLoading}
          />
        </Box>
      )}
      {/* Categories Section */}
      <Box
        as='section'
        aria-labelledby='headingCategories'
        px={{ base: 4, md: 6 }}
        py={{ base: 6, md: 8 }}
      >
        <Heading
          as='h2'
          id='headingCategories'
          variant={{ base: 'headingMedium', md: 'headingLarge' }}
          textAlign='center'
          mb={{ base: 6, md: 8 }}
        >
          <FormattedMessage defaultMessage='تصفح حسب الفئة' id='homepage.categories.heading' />
        </Heading>

        {/* Mobile: Horizontal Tabs with Icons */}
        <Box
          display={{ base: 'block', md: 'none' }}
          overflowX='auto'
          overflowY='hidden'
          pb={3}
          px={{ base: 2, sm: 4 }}
          sx={{
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'dark.D200',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'dark.D300',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'dark.D200 transparent',
          }}
          dir='rtl'
        >
          <Flex
            direction='row'
            gap={4}
            justifyContent='space-between'
            alignItems='flex-start'
            py={1}
            dir='rtl'
            width='100%'
          >
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Flex
                  key={category.id}
                  direction='column'
                  align='center'
                  gap={1.5}
                  flex={1}
                  minW='56px'
                >
                  <CategoryCard
                    title={category.title}
                    count={category.count}
                    icon={<IconComponent size={24} />}
                    isSelected={selectedCategory === category.id}
                    onClick={() => handleCategorySelect(category.id)}
                  />
                  <Heading
                    as='h3'
                    variant='headingSmall'
                    fontSize='xs'
                    fontWeight='600'
                    textAlign='center'
                    color={selectedCategory === category.id ? 'green.G500' : 'dark.D700'}
                    lineHeight='1.3'
                    noOfLines={2}
                  >
                    {category.title}
                  </Heading>
                </Flex>
              );
            })}
          </Flex>
        </Box>

        {/* Desktop: Grid Layout */}
        <Grid
          display={{ base: 'none', md: 'grid' }}
          templateColumns={{ md: `repeat(${categories.length}, 1fr)` }}
          gap={{ md: 6 }}
          maxW='1200px'
          mx='auto'
          dir='rtl'
        >
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <CategoryCard
                key={category.id}
                title={category.title}
                count={category.count}
                icon={<IconComponent size={32} />}
                isSelected={selectedCategory === category.id}
                onClick={() => handleCategorySelect(category.id)}
              />
            );
          })}
        </Grid>
      </Box>

      {/* Filtered Events Section */}
      <Box
        as='section'
        aria-labelledby='headingFilteredEvents'
        width='100vw'
        px={{ base: 4, md: 6 }}
        py={{ base: 6, md: 8 }}
      >
        <Heading
          as='h2'
          id='headingFilteredEvents'
          variant={{ base: 'headingSmall', md: 'headingMedium' }}
          textAlign='center'
          mb={{ base: 6, md: 8 }}
        >
          {categories.find((cat) => cat.id === selectedCategory)?.title}
        </Heading>

        {selectedCategory === 'international' && isInternationalCompactList ? (
          <InternationalFilteredEvents
            items={internationalItems}
            isLoading={isInternationalLoading}
          />
        ) : (
          <EventList
            concerts={nonPreviewConcerts}
            workshops={nonPreviewWorkshops}
            matches={nonPreviewMatches}
            // Card mode mixes international events in; compact mode keeps them out
            // of these grids (they render in the compact section / tab instead).
            // Gate on the resolved platform flag too, so a disabled platform (e.g. Super QI
            // with WEB/unset mode) never leaks international cards into the main grid.
            international={
              isInternationalEnabled && !isInternationalCompactList ? internationalItems : []
            }
            selectedCategory={selectedCategory}
            isLoading={isLoading || isInternationalLoading}
          />
        )}
      </Box>

      {/* International Events — additive, below all local content */}
      <InternationalEventsSection />
    </Box>
  );
};
